import { useEffect, useMemo, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, IconButton, ListSubheader, MenuItem, SxProps } from '@mui/material'
import { User } from '@/@data'
import type { UserStatusEnum, IUser, UserTypeEnum } from '@/@data'
import { useRouteQuery, useTranslator, useDebounce } from '@/hooks'
import { InputField } from '../common'
import StatusIndicator from '../common/StatusIndicator'
import { _ } from 'numeral'

interface UserSelectionProps {
  value?: string | string[] | number | number[]
  multiple?: boolean
  onChange?: (value: string, selected?: IUser) => void
  disabled?: boolean
  required?: boolean
  label?: string
  placeholder?: string
  className?: string
  fullWidth?: boolean
  onClear?: () => void
  height?: string | number
  error?: boolean
  helperText?: string
  sx?: SxProps
  status?: UserStatusEnum
  showEndAdornment?: boolean
  user_type?: UserTypeEnum
  company_id?: number
}

const UserSelection = ({
  value = '',
  multiple = false,
  onChange,
  onClear,
  disabled = false,
  label = '',
  placeholder = '',
  error = false,
  helperText = '',
  status,
  user_type,
  company_id,
  required = false,
  showEndAdornment = true
}: UserSelectionProps) => {
  const { serializeQuery } = useRouteQuery()
  const [search, setSearch] = useState('')
  const { __ } = useTranslator()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const debouncedSearch = useDebounce(search, 500)

  const { data } = useQuery({
    ...User.objects.paginateQueryFactory(
      serializeQuery({
        keywords: debouncedSearch,
        user_type,
        company_id,
        status
      })
    )
  })

  const { data: selectedBranches } = useQuery({
    ...User.objects.paginateQueryFactory(
      serializeQuery({
        user_ids: Array.isArray(value) ? value : [value]
      })
    ),
    enabled: !!value && (Array.isArray(value) ? value.length > 0 : true)
  })

  useEffect(() => {
    if (data) {
      setTimeout(() => searchInputRef.current?.focus())
    }
  }, [data])

  const options = useMemo(() => {
    const paginateOptions = data ? User.getUsersSelectOptions(data.data) : []
    const selectedOptions = selectedBranches ? User.getUsersSelectOptions(selectedBranches.data) : []

    const combinedOptions = [...paginateOptions]

    selectedOptions.forEach(option => {
      if (!combinedOptions.some(opt => opt.value === option.value)) {
        combinedOptions.push(option)
      }
    })

    return combinedOptions
  }, [data, selectedBranches])

  return (
    <>
      <InputField
        select
        value={value || (multiple ? [] : '')}
        label={
          required ? (
            <>
              {label}
              <span style={{ color: 'red' }}>*</span>
            </>
          ) : (
            label
          )
        }
        onChange={e => {
          onChange?.(
            e.target.value,
            data?.data.find(b => b.id === Number(e.target.value))
          )
        }}
        fullWidth
        error={error}
        helperText={helperText}
        disabled={disabled}
        slotProps={{
          input: {
            endAdornment:
              showEndAdornment &&
              onClear &&
              value &&
              ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value)) ? (
                <Box mr={5}>
                  <IconButton
                    size='small'
                    onClick={() => {
                      onClear()
                    }}
                    aria-label='clear selection'
                    tabIndex={-1}
                  >
                    <i className='tabler-x text-[16px] text-textPrimary'></i>
                  </IconButton>
                </Box>
              ) : null
          },
          select: {
            multiple,
            displayEmpty: true,
            renderValue: (selected: any) => {
              if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                return <span style={{ color: '#aaa' }}>{placeholder || label || __('Select User')}</span>
              }

              if (multiple && Array.isArray(selected)) {
                const selectedOptions = options.filter(option => selected.map(Number).includes(Number(option.value)))

                return (
                  <Box display='flex' alignItems='center' flexWrap='wrap' gap={0.5}>
                    {selectedOptions.map((option, index) => (
                      <Box key={option.value} display='flex' alignItems='center'>
                        {option.label}
                        {option.status && <StatusIndicator status={option.status} />}
                        {index < selectedOptions.length - 1 && <span>, </span>}
                      </Box>
                    ))}
                  </Box>
                )
              }

              const found = options.find(option => Number(option.value) === Number(selected))

              return (
                <Box display='flex' alignItems='center' gap={2}>
                  {found?.status && <StatusIndicator status={found.status} />}
                  {found ? found.label : selected}
                </Box>
              )
            }
          }
        }}
      >
        <ListSubheader onKeyDown={e => e.stopPropagation()}>
          <InputField
            fullWidth
            defaultValue={search}
            placeholder={__('Search User')}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
            inputRef={searchInputRef}
          />
        </ListSubheader>
        {options.map(option => (
          <MenuItem onKeyDown={e => e.stopPropagation()} key={option.value} value={option.value}>
            <Box display='flex' alignItems='center' gap={2}>
              {option.status && <StatusIndicator status={option.status} />}

              {option.label}
            </Box>
          </MenuItem>
        ))}
      </InputField>
    </>
  )
}

export default UserSelection
